import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import HsnChooser, { HsnListButton, useHsnCodes } from '../components/HsnChooser';
import MrpRecommendationImportDialog from '../components/MrpRecommendationImportDialog';
import { departmentSelectOptions, coerceLegacyDepartmentToFy } from '../utils/poDepartmentOptions';
import { SapEmployeeSelectWithArrow, ActiveUserSelectWithArrow, SapYellowArrow } from '../components/SapNamePickFields';

/* ─────────────── ThreeDotsPicker ─────────────── */
const ThreeDotsPicker = ({ onClick, title }) => (
  <button type="button" onClick={onClick} title={title}
    className="absolute right-1.5 top-1/2 -translate-y-1/2 px-1 text-sap-textLight hover:text-sap-blue transition-colors">
    <span className="text-base leading-none">⋯</span>
  </button>
);

/* ─────────────── ProjectPickerDialog ─────────────── */
const ProjectPickerDialog = ({ current, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [newCode, setNewCode] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createErr, setCreateErr] = React.useState('');

  const reload = useCallback(async (q) => {
    try {
      setLoading(true);
      const r = await api.get(`/projects?search=${encodeURIComponent(q || '')}`);
      setRows(r || []);
    } catch (e) { setError(e.message || 'Failed to load projects'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) reload(search); }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, reload]);

  const handleChoose = () => { if (selected) onSelect(selected); };
  const handleNew = async () => {
    if (!newCode.trim()) { setCreateErr('Project code is required'); return; }
    setCreating(true); setCreateErr('');
    try {
      const created = await api.post('/projects', { project_code: newCode.trim(), project_name: newName.trim() });
      setShowNew(false); setNewCode(''); setNewName('');
      await reload(search);
      onSelect(created);
    } catch (e) { setCreateErr(e.message || 'Failed to create project'); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[720px] max-h-[560px] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">List of Projects</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Find project code or name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="py-10 text-center text-xs text-sap-textLight">Loading…</div>
            : error ? <div className="py-10 text-center text-xs text-red-600">{error}</div>
              : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                    <tr>
                      <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-40">Project Code</th>
                      <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Project Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p, i) => {
                      const isSel = selected && String(selected.project_code) === String(p.project_code);
                      const isCur = String(p.project_code) === String(current);
                      return (
                        <tr key={p.project_code || i} onClick={() => setSelected(p)} onDoubleClick={() => onSelect(p)}
                          className={`border-t border-sap-border/20 cursor-pointer transition-colors ${isSel ? 'bg-sap-blue/15 ring-1 ring-sap-blue/30' : isCur ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}>
                          <td className="px-3 py-2 font-mono font-semibold text-sap-blue">{p.project_code}</td>
                          <td className="px-3 py-2 text-sap-text">{p.project_name || '—'}</td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && <tr><td colSpan={2} className="px-3 py-6 text-center text-sap-textLight">No projects found</td></tr>}
                  </tbody>
                </table>
              )}
        </div>
        {showNew && (
          <div className="px-4 py-3 border-t border-sap-border bg-blue-50/50 space-y-2">
            <div className="text-xs font-semibold text-sap-text">New Project</div>
            <div className="flex gap-2">
              <input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="Project Code *"
                className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Project Name"
                className="flex-[2] h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
            </div>
            {createErr && <div className="text-2xs text-red-600">{createErr}</div>}
            <div className="flex gap-2">
              <button onClick={handleNew} disabled={creating} className="px-3 py-1 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50">
                {creating ? 'Creating…' : 'Create'}</button>
              <button onClick={() => { setShowNew(false); setCreateErr(''); }} className="px-3 py-1 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        )}
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between">
          <span className="text-2xs text-sap-textLight">{rows.length} project{rows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={handleChoose} disabled={!selected} className="btn-primary-sm disabled:opacity-40">Choose</button>
            <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-100 transition-all">Cancel</button>
            <button onClick={() => setShowNew(v => !v)} className="px-4 py-1.5 text-xs font-semibold border border-sap-blue text-sap-blue rounded-lg hover:bg-sap-blue/5 transition-all">New</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── BusinessPartnerProjectField ─────────────── */
function BusinessPartnerProjectField({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <div className="relative">
        <input value={value || ''} onChange={e => onChange(e.target.value)}
          className="w-full h-8 px-3 pr-7 text-xs border border-sap-border rounded-lg focus:outline-none focus:border-sap-blue bg-white" />
        <ThreeDotsPicker title="Choose Project" onClick={() => setOpen(true)} />
      </div>
      {open && (
        <ProjectPickerDialog current={value}
          onSelect={(p) => { onChange(p.project_code || ''); setOpen(false); }}
          onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/* ═══════════════════ SapReferencePickerDialog (Tax / Distribution) ═══════════════════ */
const SapReferencePickerDialog = ({ type, current, onSelect, onClose }) => {
  const isTax = type === 'tax';
  const title = isTax ? 'Tax Codes' : 'Distribution Rules';
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(current || '');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = isTax ? '/sap-reference/tax-codes' : '/sap-reference/distribution-rules';
        const data = await api.get(`${path}?search=${encodeURIComponent(search)}&limit=200`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search, isTax]);

  const codeKey = isTax ? 'tax_code' : 'rule_code';
  const nameKey = isTax ? 'tax_name' : 'description';

  return (
    <div className="fixed inset-0 z-[100070] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[640px] max-h-[520px] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">{title}</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isTax ? 'Search code or name…' : 'Search rule or description…'}
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
              <tr>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-28">Code</th>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Name / Description</th>
                {isTax && (
                  <th className="text-right px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-16">Rate %</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isTax ? 3 : 2} className="px-3 py-4 text-center text-sap-textLight">Loading…</td></tr>
              )}
              {!loading && rows.map((r) => {
                const code = r[codeKey];
                const isCurrent = String(code) === String(selected);
                return (
                  <tr
                    key={code}
                    onClick={() => setSelected(code)}
                    onDoubleClick={() => onSelect(r)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}
                  >
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{r[nameKey] || '—'}</td>
                    {isTax && (
                      <td className="px-3 py-1.5 text-right text-sap-textLight">{Number(r.rate_pct).toFixed(2)}</td>
                    )}
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isTax ? 3 : 2} className="px-3 py-4 text-center text-sap-textLight">No records — import master data Excel files</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex justify-between items-center">
          <span className="text-2xs text-sap-textLight">Double-click or Choose • {rows.length} row{rows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1 text-xs border border-sap-border rounded-lg hover:bg-gray-100">Cancel</button>
            <button type="button" disabled={!selected}
              onClick={() => {
                const row = rows.find((r) => String(r[codeKey]) === String(selected));
                if (row) onSelect(row);
              }}
              className="btn-primary-sm disabled:opacity-40">Choose</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ SapReferenceCell (input + golden arrow) ═══════════════════ */
const SapReferenceCell = ({ value, onChange, onOpen, title, width = 'w-20' }) => (
  <td className="px-1 py-1">
    <div className="flex items-center gap-0.5">
      <SapYellowArrow onClick={onOpen} title={title} />
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`${width} border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue`}
      />
    </div>
  </td>
);

/* ═══════════════════ ProjectCell (input + 3 dots picker) ═══════════════════ */
function ProjectCell({ value, onChange }) {
  const [open, setOpen] = React.useState(false);
  return (
    <td className="px-1 py-1">
      <div className="relative">
        <input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-24 border border-sap-border rounded pr-7 pl-2 py-1 text-xs focus:outline-none focus:border-sap-blue bg-white"
        />
        <ThreeDotsPicker title="Choose Project" onClick={() => setOpen(true)} />
      </div>
      {open && (
        <ProjectPickerDialog
          current={value}
          onSelect={(p) => { onChange(p.project_code || ''); setOpen(false); }}
          onClose={() => setOpen(false)}
        />
      )}
    </td>
  );
}

/* ═══════════════════ constants ═══════════════════ */
const TABS = ['Contents', 'Logistics', 'Accounting', 'Attachments'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'JPY', 'GBP'];
const STATUSES = ['Draft', 'Open', 'Closed', 'Cancelled'];
const UOM_OPTIONS = ['Each', 'Kg', 'Lb', 'Box', 'Pallet', 'Meter', 'Foot', 'Liter', 'Gallon', 'Set'];
const PAY_TERMS = ['', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90', 'Due on Receipt', 'COD'];
const PAY_METHODS = ['', 'Bank Transfer', 'Check', 'Cash', 'Credit Card', 'Letter of Credit'];
const LANGUAGES = ['', 'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Marathi', 'Bengali', 'Gujarati'];
const SHIPPING_TYPES = ['', 'Air', 'Road', 'Rail', 'Sea', 'Courier'];
const LINE_TYPES = ['Item', 'Service'];
const ITEM_SERVICE_TYPES = ['Item', 'Service'];
const SUMMARY_TYPES = ['No Summary', 'Summary', 'Detailed Summary'];

const STATUS_COLORS = {
  Draft: 'bg-[#F0F4F3] text-[#6B7F7F]',
  Open: 'bg-[#EDF5F0] text-[#4A9E6E]',
  Closed: 'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};

const today = () => new Date().toISOString().slice(0, 10);

const blankLine = () => ({
  _key: Date.now() + Math.random(),
  line_type: 'Item', item_id: '', item_no: '', item_desc: '', bp_catalog_no: '',
  quantity: '1.0000', uom: 'Each', unit_price: '0.0000', discount_pct: '0.00',
  tax_code: '', project: '', open_qty: '0.0000', in_stock: '0.0000',
  committed: '0.0000', ordered: '0.0000',
  country_of_origin: '', blanket_agreement_no: '',
  standard_item_id: '', commodity_classification: '',
  warehouse_id: '', wh_code: '', required_date: '', hsn_code: '', delivery_date: '',
  distr_rule: '',
});

const blankPO = {
  po_number: '', series: '', series_id: '', vendor_id: '', vendor_code: '', vendor_name: '',
  contact_person: '', vendor_ref_no: '', currency: 'INR',
  posting_date: today(), delivery_date: '', document_date: today(),
  status: 'Draft', remarks: '',
  item_service_type: 'Item', summary_type: 'No Summary',
  tax_pct: '12.00', freight_charges: '0.0000',
  // Logistics
  ship_to_address: '', bill_to_address: '', shipping_type: '',
  language: 'English', split_purchase_order: false, approved: true,
  // Accounting
  journal_remark: '', payment_terms: '', payment_method: '', central_bank_ind: '',
  due_date_type: '', due_date_months: '0', due_date_days: '0',
  cash_discount_offset: '0', expense_account: '',
  bp_project: '', create_qr_code_from: '',
  cancellation_date: '', required_date: '', indicator: '',
  federal_tax_id: '', order_number: '', referenced_document: '',
  // Footer
  buyer: '', owner: '', discount_pct: '0.00',
  rounding: false, rounding_amount: '0.0000',
  // Legacy
  is_time_bound: 0, contract_start: '', contract_end: '',
  vendor_sla_days: '', penalty_clause: '',
  department: '',
  approver_name: '',
};

const fmt4 = (v) => isNaN(+v) ? '0.0000' : Number(v).toFixed(4);
const fmt2 = (v) => isNaN(+v) ? '0.00' : Number(v).toFixed(2);
const fmtCurrency = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ═══════════════════ inputs ═══════════════════ */
const inp = `w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white
             focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue`;
const sel = `${inp} appearance-none`;
const lbl = 'block text-[10px] font-medium text-sap-textLight mb-0.5 whitespace-nowrap';

// A date input shows a calendar icon, but its segments are typeable and the year
// segment happily takes "226". One purchase order line reached SAP asking for
// delivery in the year 0226 that way. Bounds make the browser refuse it; the server
// refuses it too, because not every write comes from this screen.
const DATE_BOUNDS = { min: '2000-01-01', max: '2100-12-31' };

/**
 * A stored date the bounds would refuse, blanked so it can be replaced.
 *
 * Orders saved before those bounds existed can hold a year like 0226. The server now
 * rejects such a value, which left one of them unsaveable: the offending field is the
 * line's PO Delivery Date, thirteen columns into a grid that scrolls sideways, so the
 * error named a field the planner could not see and every save failed. Clearing it on
 * load puts an empty box in front of them instead, and an empty date is honest where
 * the year 226 was not.
 */
function usableDate(v) {
  const d = v ? String(v).slice(0, 10) : '';
  if (!d || d === '0000-00-00' || d === 'null') return '';
  const year = Number(d.slice(0, 4));
  return year >= 2000 && year <= 2100 ? d : '';
}
const chk = 'accent-sap-blue w-3.5 h-3.5';

// Filled from /purchase-orders/approvers. No hardcoded names: a fixed list here kept
// showing renamed approvers ("Kamkal Kishor") until the page had loaded the real one.
const DEFAULT_APPROVERS = [];

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm] = useState({ ...blankPO });
  const [lines, setLines] = useState([blankLine()]);

  // ── Approver from item group ──
  // Client rule (17 Sep): BO / RM items are approved by Sachin Jadhav, MFG and service
  // items by Santosh Chavan. The approver fills in as lines are added, and picking
  // someone else asks first — the approval email goes to the approver shown here, so
  // the field and the inbox must not quietly disagree.
  const [approverRule, setApproverRule] = useState({ approvers: [], groups: [], mixed: false });
  const approverOverridden = useRef(false);
  // The item set an existing PO was saved with. Opening a PO must not rewrite its
  // approver; only a change to its items does.
  const loadedItemKey = useRef(null);
  const itemKey = useMemo(
    () => [...new Set(lines.map((l) => l.item_id).filter(Boolean).map(String))].sort().join(','),
    [lines]
  );
  // The distribution rule decides the approver on its own when it is SP, so changing
  // it has to re-ask just as adding an item does.
  const distrKey = useMemo(
    () => [...new Set(lines.map((l) => String(l.distr_rule || '').trim().toUpperCase()).filter(Boolean))]
      .sort().join(','),
    [lines]
  );

  useEffect(() => {
    if (!itemKey && !distrKey) {
      setApproverRule({ approvers: [], groups: [], mixed: false });
      return undefined;
    }
    let cancelled = false;
    api.post('/purchase-orders/approver-for-items', {
      item_ids: itemKey ? itemKey.split(',') : [],
      distr_rules: distrKey ? distrKey.split(',') : [],
    })
      .then((d) => { if (!cancelled && d) setApproverRule(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [itemKey, distrKey]);

  useEffect(() => {
    if (approverRule.approvers.length !== 1) return;
    const suggested = approverRule.approvers[0];
    // A distribution rule answers the question outright, so it fills the approver in
    // whatever was there before - including a name somebody chose by hand. Without
    // that, setting SP on an order that already had an approver would change nothing,
    // and the client asked not to have to select the approver at all.
    const decisive = approverRule.decided_by === 'distribution_rule';
    if (!decisive) {
      if (approverOverridden.current) return;
      if (!isNew && loadedItemKey.current === itemKey) return;
    }
    setForm((f) => (f.approver_name === suggested ? f : { ...f, approver_name: suggested }));
  }, [approverRule, itemKey, isNew]);
  const [attachments, setAttachments] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [sendingSapApproval, setSendingSapApproval] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [sapSyncMeta, setSapSyncMeta] = useState({
    status: '',
    docEntry: null,
    docNum: null,
    // A purchase order sent to SAP waits there as a draft for its approvers, so
    // until they approve and add it there is a draft but no document number.
    draftEntry: null,
    approval: '',
    error: '',
  });
  const [showMrpImport, setShowMrpImport] = useState(false);
  const [refPicker, setRefPicker] = useState(null);

  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin' || !!user?.can_manage_users;

  const [approverOptions, setApproverOptions] = useState(DEFAULT_APPROVERS);
  const [showAddApproverModal, setShowAddApproverModal] = useState(false);
  const [adminEmployees, setAdminEmployees] = useState([]);
  const [loadingAdminEmployees, setLoadingAdminEmployees] = useState(false);
  const [newApproverName, setNewApproverName] = useState('');
  const [empSearch, setEmpSearch] = useState('');
  const [addingApprover, setAddingApprover] = useState(false);

  useEffect(() => {
    api.get('/purchase-orders/approvers')
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setApproverOptions(data);
        }
      })
      .catch(() => {});
  }, []);

  const openAddApproverModal = () => {
    if (!isSuperAdmin) {
      alert('Only Super Admin users can add new approvers.');
      return;
    }
    setShowAddApproverModal(true);
    setNewApproverName('');
    setEmpSearch('');
    if (adminEmployees.length === 0) {
      setLoadingAdminEmployees(true);
      api.get('/purchase-orders/users')
        .then(data => {
          if (Array.isArray(data)) setAdminEmployees(data);
        })
        .catch(() => {})
        .finally(() => setLoadingAdminEmployees(false));
    }
  };

  const handleSaveNewApprover = async () => {
    if (!isSuperAdmin) {
      alert('Only Super Admin users can add new approvers.');
      return;
    }
    const nameToSave = newApproverName.trim();
    if (!nameToSave) {
      alert('Please enter or select an approver name.');
      return;
    }
    setAddingApprover(true);
    try {
      const res = await api.post('/purchase-orders/approvers', { name: nameToSave });
      if (Array.isArray(res)) {
        setApproverOptions(res);
      } else if (!approverOptions.includes(nameToSave)) {
        setApproverOptions(prev => [...prev, nameToSave]);
      }
      setForm(prev => ({ ...prev, approver_name: nameToSave }));
      setShowAddApproverModal(false);
    } catch (err) {
      alert(err?.response?.data?.message || err.message || 'Failed to add approver');
    } finally {
      setAddingApprover(false);
    }
  };
  const [taxRateByCode, setTaxRateByCode] = useState({});

  const [vendorResults, setVendorResults] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDD, setShowVendorDD] = useState(false);
  const [vendorListLoading, setVendorListLoading] = useState(false);
  const [itemSearchIdx, setItemSearchIdx] = useState(null);
  const [itemResults, setItemResults] = useState([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDD, setShowItemDD] = useState(false);
  /** Fixed dropdown position for portal (escapes table overflow clipping). */
  const [itemDDRect, setItemDDRect] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [poSeriesList, setPoSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const itemBlurTimerRef = useRef(null);
  const activeItemInputRef = useRef(null);
  const vendorWrapRef = useRef(null);
  const vendorDdSearchRef = useRef(null);

  useLayoutEffect(() => {
    if (!showItemDD || itemSearchIdx === null) {
      setItemDDRect(null);
      return;
    }
    const updateRect = () => {
      const el = activeItemInputRef.current;
      if (!el || !(el instanceof HTMLElement)) {
        setItemDDRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const width = Math.min(Math.max(r.width * 5, 260), 448, window.innerWidth - 16);
      const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      const spaceBelow = window.innerHeight - r.bottom - 10;
      const maxHeight = Math.min(280, Math.max(96, spaceBelow));
      setItemDDRect({ top: r.bottom + 4, left, width, maxHeight });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [showItemDD, itemSearchIdx, itemResults, itemSearchLoading, itemSearch]);

  /* ── Load warehouses ── */
  useEffect(() => {
    api.get('/purchase-orders/warehouses')
      .then(r => setWarehouses(r || []))
      .catch(() => { });
  }, []);

  /* ── Load tax codes once for accurate line-level tax calculation in totals ── */
  useEffect(() => {
    let cancelled = false;
    api.get('/sap-reference/tax-codes?limit=500')
      .then((rows) => {
        if (cancelled) return;
        const next = {};
        for (const row of Array.isArray(rows) ? rows : []) {
          const code = String(row?.tax_code || '').trim().toUpperCase();
          if (!code) continue;
          const rate = parseFloat(row?.rate_pct);
          next[code] = Number.isFinite(rate) ? rate : 0;
        }
        setTaxRateByCode(next);
      })
      .catch(() => {
        if (!cancelled) setTaxRateByCode({});
      });
    return () => { cancelled = true; };
  }, []);

  /* ── Load PO series list ── */
  useEffect(() => {
    api.get('/purchase-orders/series')
      .then(list => {
        setPoSeriesList(list || []);
        if (isNew && list?.length) {
          const def = list.find(s => s.is_default) || list[0];
          setSelectedSeriesId(def.series_id);
          setForm(f => ({ ...f, series: def.series_name, series_id: def.series_id }));
          // Fetch next number for default series
          api.get(`/purchase-orders/next-number?series_id=${def.series_id}`)
            .then(r => setForm(f => ({ ...f, po_number: r?.next || '' })))
            .catch(() => { });
        }
      })
      .catch(() => { });
  }, []);

  /* ── Handle series change ── */
  const handleSeriesChange = (seriesId) => {
    const sid = parseInt(seriesId, 10);
    const chosen = poSeriesList.find(s => s.series_id === sid);
    if (!chosen) return;
    setSelectedSeriesId(sid);
    setForm(f => ({ ...f, series: chosen.series_name, series_id: sid }));
    if (isNew) {
      api.get(`/purchase-orders/next-number?series_id=${sid}`)
        .then(r => setForm(f => ({ ...f, po_number: r?.next || '' })))
        .catch(() => { });
    }
  };

  /* ── Load existing PO ── */
  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const data = await api.get(`/purchase-orders/${id}`);
        const h = { ...blankPO };
        for (const k of Object.keys(blankPO)) {
          if (data[k] !== undefined && data[k] !== null) {
            if (typeof blankPO[k] === 'boolean') h[k] = !!data[k];
            else h[k] = data[k];
          }
        }
        h.vendor_code = data.vendor_code || '';
        h.vendor_name = data.vendor_name || '';
        h._po_id = data.po_id;
        const cleared = [];
        for (const dk of ['posting_date', 'delivery_date', 'document_date', 'cancellation_date', 'required_date', 'contract_start', 'contract_end']) {
          const raw = data[dk] ? String(data[dk]).slice(0, 10) : '';
          h[dk] = usableDate(raw);
          if (raw && !h[dk] && raw !== '0000-00-00' && raw !== 'null') {
            cleared.push(`${dk.replace(/_/g, ' ')} (${raw})`);
          }
        }
        h.tax_pct = fmt2(data.tax_pct);
        h.freight_charges = fmt4(data.freight_charges);
        h.discount_pct = fmt2(data.discount_pct);
        h.rounding_amount = fmt4(data.rounding_amount);
        h.department = coerceLegacyDepartmentToFy(
          data.department != null && data.department !== '' ? String(data.department) : h.department || ''
        );
        setSapSyncMeta({
          status: data.sap_sync_status || '',
          docEntry: data.sap_doc_entry ?? null,
          docNum: data.sap_doc_num ?? null,
          draftEntry: data.sap_draft_entry ?? null,
          approval: data.sap_approval_status || '',
          error: data.sap_sync_error || '',
        });
        setForm(h);
        loadedItemKey.current = [...new Set((data.lines || []).map((l) => l.item_id).filter(Boolean).map(String))].sort().join(',');
        approverOverridden.current = false;
        setLines((data.lines || []).map(l => ({
          _key: l.line_id,
          line_type: l.line_type || 'Item',
          item_id: l.item_id, item_no: l.item_no, item_desc: l.item_desc,
          bp_catalog_no: l.bp_catalog_no || '',
          quantity: fmt4(l.quantity), uom: l.uom,
          unit_price: fmt4(l.unit_price), discount_pct: fmt2(l.discount_pct),
          tax_code: l.tax_code || '', project: l.project || '',
          open_qty: fmt4(l.open_qty), in_stock: fmt4(l.in_stock),
          committed: fmt4(l.committed), ordered: fmt4(l.ordered),
          country_of_origin: l.country_of_origin || '',
          blanket_agreement_no: l.blanket_agreement_no || '',
          standard_item_id: l.standard_item_id || '',
          commodity_classification: l.commodity_classification || '',
          warehouse_id: l.warehouse_id || '', wh_code: l.wh_code || '',
          required_date: usableDate(l.required_date),
          hsn_code: l.hsn_code || '',
          delivery_date: usableDate(l.delivery_date),
          distr_rule: l.distr_rule || '',
        })));

        (data.lines || []).forEach((l, i) => {
          for (const [field, raw] of [['delivery date', l.delivery_date], ['required date', l.required_date]]) {
            const v = raw ? String(raw).slice(0, 10) : '';
            if (v && !usableDate(v) && v !== '0000-00-00') {
              cleared.push(`line ${i + 1} ${field} (${v})`);
            }
          }
        });
        if (cleared.length) {
          setError(`This order held ${cleared.length === 1 ? 'a date' : 'dates'} that cannot be used: `
            + `${cleared.join(', ')}. ${cleared.length === 1 ? 'That field has' : 'Those fields have'} `
            + 'been cleared — set the right date, or leave it blank, and save.');
        }
        setAttachments(data.attachments || []);
      } catch {
        setError('Failed to load purchase order');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  /* ── Vendor search (dropdown open loads list; empty search = first 500 from API) ── */
  useEffect(() => {
    if (!showVendorDD) return;
    const t = setTimeout(async () => {
      setVendorListLoading(true);
      try {
        const r = await api.get(`/purchase-orders/vendors?search=${encodeURIComponent(vendorSearch.trim())}`);
        setVendorResults(Array.isArray(r) ? r : []);
      } catch {
        setVendorResults([]);
      } finally {
        setVendorListLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [vendorSearch, showVendorDD]);

  const vendorFieldBlurLater = () => {
    setTimeout(() => {
      if (!vendorWrapRef.current?.contains(document.activeElement)) {
        setShowVendorDD(false);
      }
    }, 200);
  };

  const pickVendor = (v) => {
    setForm(f => ({
      ...f, vendor_id: v.vendor_id, vendor_code: v.vendor_code,
      vendor_name: v.vendor_name, contact_person: v.contact_person || f.contact_person,
      payment_terms: v.payment_terms || f.payment_terms,
      currency: v.currency || f.currency,
    }));
    setVendorSearch(''); setShowVendorDD(false);
  };

  /* ── Item search (runs when dropdown open — empty query loads first Active purchasable items) ── */
  useEffect(() => {
    if (!showItemDD || itemSearchIdx === null) {
      setItemResults([]);
      setItemSearchLoading(false);
      return;
    }
    const q = itemSearch.trim();
    const delay = q.length < 1 ? 80 : 250;
    let cancelled = false;
    setItemSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/purchase-orders/items?search=${encodeURIComponent(q)}`);
        if (!cancelled) setItemResults(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setItemResults([]);
      } finally {
        if (!cancelled) setItemSearchLoading(false);
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [itemSearch, itemSearchIdx, showItemDD]);

  /* ── Totals ── */
  const { subtotal, discountAmt, totalBeforeDiscount, taxAmt, freight, roundingVal, grandTotal } = useMemo(() => {
    const totalBeforeDiscount = lines.reduce((sum, l) => {
      const qty = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      const disc = parseFloat(l.discount_pct) || 0;
      return sum + qty * price * (1 - disc / 100);
    }, 0);
    const discPct = parseFloat(form.discount_pct) || 0;
    const discountAmt = totalBeforeDiscount * discPct / 100;
    const subtotal = totalBeforeDiscount - discountAmt;

    // Prefer per-line tax code rates (SAP reference) and fall back to header tax_pct.
    const headerTaxPct = parseFloat(form.tax_pct) || 0;
    const headerMultiplier = 1 - discPct / 100;
    let lineTaxTotal = 0;
    let hasAnyLineTaxCode = false;
    for (const l of lines) {
      const qty = parseFloat(l.quantity) || 0;
      const price = parseFloat(l.unit_price) || 0;
      const lineDisc = parseFloat(l.discount_pct) || 0;
      const lineSubtotal = qty * price * (1 - lineDisc / 100);
      const discountedLineBase = lineSubtotal * headerMultiplier;
      const code = String(l.tax_code || '').trim().toUpperCase();
      if (code) hasAnyLineTaxCode = true;
      const resolvedRate = code && taxRateByCode[code] != null
        ? parseFloat(taxRateByCode[code]) || 0
        : headerTaxPct;
      lineTaxTotal += discountedLineBase * (resolvedRate / 100);
    }
    const taxAmt = hasAnyLineTaxCode ? lineTaxTotal : (subtotal * headerTaxPct / 100);

    const freight = parseFloat(form.freight_charges) || 0;
    const roundingVal = form.rounding ? (parseFloat(form.rounding_amount) || 0) : 0;
    return { subtotal, discountAmt, totalBeforeDiscount, taxAmt, freight, roundingVal, grandTotal: subtotal + taxAmt + freight + roundingVal };
  }, [lines, form.tax_pct, form.freight_charges, form.discount_pct, form.rounding, form.rounding_amount, taxRateByCode]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const poDepartmentOpts = useMemo(() => departmentSelectOptions(form.department || ''), [form.department]);

  const fillFromIssue = useCallback(async (issueNumber) => {
    try {
      const data = await api.get(`/production-orders/issues/by-number/${encodeURIComponent(issueNumber)}`);
      if (!data || !data.issue_id) return;

      setForm(f => ({
        ...f,
        po_number: issueNumber,
        posting_date: data.posting_date ? String(data.posting_date).slice(0, 10) : f.posting_date,
        remarks: `From Issue ${data.issue_number} / ${data.order_no || ''}`.trim(),
        journal_remark: 'Issue for Production',
        referenced_document: data.issue_number || issueNumber,
        bp_project: data.project_no || f.bp_project,
      }));

      if (Array.isArray(data.lines) && data.lines.length > 0) {
        setLines(data.lines.filter(l => l.line_type === 'Item' && l.item_id).map(l => ({
          ...blankLine(),
          _key: Date.now() + Math.random(),
          line_type: 'Item',
          item_id: l.item_id,
          item_no: l.item_no || '',
          item_desc: l.item_desc || l.description || '',
          quantity: fmt4(l.quantity),
          unit_price: fmt4(l.unit_cost),
          uom: l.uom_name || 'Each',
          warehouse_id: l.warehouse_id || '',
          wh_code: l.wh_code || '',
          in_stock: fmt4(l.in_stock),
        })));
      }
    } catch (err) {
      console.warn('Issue lookup failed:', err.message);
    }
  }, []);

  const updateLine = (idx, k, v) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { ...blankLine(), delivery_date: form.delivery_date || '' }]);
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  const pickItem = (item) => {
    if (itemBlurTimerRef.current) {
      clearTimeout(itemBlurTimerRef.current);
      itemBlurTimerRef.current = null;
    }
    if (itemSearchIdx === null) return;
    setLines(ls => ls.map((l, i) => i === itemSearchIdx ? {
      ...l,
      item_id: item.item_id, item_no: item.item_no, item_desc: item.description,
      bp_catalog_no: item.bp_catalog_hint != null && String(item.bp_catalog_hint).trim()
        ? String(item.bp_catalog_hint).trim()
        : l.bp_catalog_no,
      uom: 'Each', unit_price: fmt4(item.unit_price || 0),
      hsn_code: item.hsn_code || l.hsn_code || '',
    } : l));
    setShowItemDD(false); setItemSearch(''); setItemSearchIdx(null);
  };

  const poContentsLocked = form.status === 'Closed' || form.status === 'Cancelled';

  const applyMrpImportLines = (recs, run) => {
    const proj =
      (form.bp_project && String(form.bp_project).trim())
      || (run?.project_number != null && String(run.project_number).trim() !== '' ? String(run.project_number) : '');
    setLines((ls) => {
      const kept = ls.filter((l) => l.item_id);
      const added = recs.map((rec) => {
        const l = blankLine();
        let req = '';
        if (rec.required_date != null && rec.required_date !== '') {
          const d = rec.required_date;
          req = typeof d === 'string' ? d.slice(0, 10) : '';
        }
        return {
          ...l,
          _key: `${Date.now()}-${Math.random()}`,
          item_id: rec.item_id,
          item_no: rec.item_no || '',
          item_desc: rec.item_desc || '',
          hsn_code: rec.hsn_code || '',
          quantity: fmt4(rec.recommended_qty),
          uom: rec.uom_name || rec.uom || 'Each',
          unit_price: fmt4(rec.unit_price != null ? rec.unit_price : 0),
          open_qty: fmt4(rec.recommended_qty),
          warehouse_id: rec.to_warehouse_id != null ? String(rec.to_warehouse_id) : '',
          wh_code: rec.to_wh_code || '',
          project: proj || rec.inherited_project_no || '',
          tax_code: rec.purchase_tax_group || '',
        };
      });
      return [...kept, ...added];
    });
    setSuccessMsg(`Imported ${recs.length} line(s) from MRP.`);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleSave = async (statusOverride) => {
    if (!form.vendor_id) { setError('Please select a vendor'); return; }
    const validLines = lines.filter(l => l.item_id);
    if (validLines.length === 0) { setError('Add at least one line item'); return; }

    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payload = {
        ...form,
        status: statusOverride || form.status,
        lines: validLines.map(l => ({
          line_type: l.line_type || 'Item',
          item_id: l.item_id, quantity: l.quantity, uom: l.uom,
          unit_price: l.unit_price, discount_pct: l.discount_pct,
          warehouse_id: l.warehouse_id || null, required_date: l.required_date || null,
          hsn_code: l.hsn_code || null, delivery_date: l.delivery_date || null,
          tax_code: l.tax_code || null, project: l.project || null,
          distr_rule: l.distr_rule || null,
        })),
      };
      if (isNew) {
        // Atomically consume the next number from the selected series
        if (selectedSeriesId) {
          const consumed = await api.post('/purchase-orders/consume-number', { series_id: selectedSeriesId });
          if (consumed?.consumed) {
            payload.po_number = consumed.consumed;
            payload.series = consumed.series_name;
          }
        }
        const r = await api.post('/purchase-orders', payload);
        const newId = r?.po_id;
        setSuccessMsg('Purchase order created!');
        setTimeout(() => navigate(`/purchase-order/${newId}`), 800);
      } else {
        await api.put(`/purchase-orders/${form._po_id}`, payload);
        setSuccessMsg('Saved successfully');
        setTimeout(() => setSuccessMsg(''), 2500);
        if (statusOverride) setField('status', statusOverride);
      }
    } catch (e) {
      // e.message is where it actually is: api/api.js throws a plain Error holding
      // the server's message. Without this the screen said "Failed to save" while the
      // server had named the field and the reason.
      setError(e.response?.data?.message || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this Purchase Order? This will also delete all its lines and attachments, freeing up the order number for reuse.')) return;
    
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.del(`/purchase-orders/${form._po_id}`);
      setSuccessMsg('Purchase order permanently deleted');
      setTimeout(() => navigate('/purchasing/orders'), 1000);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to delete purchase order');
      setSaving(false);
    }
  };

  const handleSendToSapApproval = async () => {
    if (isNew || !form._po_id) return;
    setSendingSapApproval(true);
    setError('');
    try {
      const data = await api.post(`/purchase-orders/${form._po_id}/send-sap-approval`, {});
      const po = data?.po || null;
      setSapSyncMeta({
        status: po?.sap_sync_status || sapSyncMeta.status || 'pending',
        docEntry: po?.sap_doc_entry ?? sapSyncMeta.docEntry,
        docNum: po?.sap_doc_num ?? sapSyncMeta.docNum,
        draftEntry: po?.sap_draft_entry ?? sapSyncMeta.draftEntry,
        approval: po?.sap_approval_status || sapSyncMeta.approval || '',
        error: po?.sap_sync_error || '',
      });
      setSuccessMsg(data?.message || 'PO queued to SAP approval');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to send PO to SAP approval');
    } finally {
      setSendingSapApproval(false);
    }
  };

  const [sendingEmailNotification, setSendingEmailNotification] = useState(false);
  // A line's HSN can be typed or chosen from SAP's list; this is the line whose list
  // is open.
  const [hsnPickerLine, setHsnPickerLine] = useState(null);
  const { codes: hsnCodes } = useHsnCodes();

  const handleSendApprovalEmail = async () => {
    if (isNew || !form._po_id) {
      alert('Please save the Purchase Order first before sending an approval email.');
      return;
    }
    setSendingEmailNotification(true);
    setError('');
    try {
      const res = await api.post(`/purchase-orders/${form._po_id}/send-email-notification`, {});
      setSuccessMsg(res?.message || 'PO Approval Notification email sent successfully!');
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to send approval email');
    } finally {
      setSendingEmailNotification(false);
    }
  };

  const sapQueuedOrSent = sapSyncMeta.status === 'pending' || sapSyncMeta.status === 'sent'
    || !!sapSyncMeta.docEntry || !!sapSyncMeta.draftEntry;
  // What SAP has done with it, in the words the purchase team uses.
  const sapStandingText = sapSyncMeta.docEntry
    ? `Added in SAP${sapSyncMeta.docNum != null ? ` as purchase order ${sapSyncMeta.docNum}` : ''}`
    : sapSyncMeta.draftEntry
      ? (sapSyncMeta.approval === 'rejected'
        ? `Rejected in SAP (draft ${sapSyncMeta.draftEntry})`
        : `Waiting for approval in SAP — draft ${sapSyncMeta.draftEntry}`
          + `${sapSyncMeta.docNum != null ? `, will be numbered when approved` : ''}`)
      : '';

  if (loading) return <div className="py-20 text-center text-xs text-sap-textLight">Loading…</div>;

  /* ═══════════════════ RENDER ═══════════════════ */
  return (
    <div className="space-y-2 pb-6">
      {/* Breadcrumb */}
      <div className="text-2xs text-sap-textLight flex items-center gap-1">
        <button onClick={() => navigate('/purchasing/orders')} className="hover:text-sap-blue transition-colors">Purchase Orders</button>
        <span>›</span>
        <span className="text-sap-text font-medium">{isNew ? 'New' : form.po_number}</span>
      </div>

      {/* Header row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="page-title">
            Purchase Order
          </h1>
          {!isNew && (
            <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${STATUS_COLORS[form.status] || ''}`}>
              {form.status}
            </span>
          )}
        </div>
        <div className="flex gap-1.5">
          {!isNew && !['Closed', 'Cancelled'].includes(form.status) && (
            <button
              onClick={handleSendToSapApproval}
              disabled={sendingSapApproval || sapQueuedOrSent}
              title={sapQueuedOrSent ? 'Already queued/sent to SAP' : 'Send this PO number to SAP for approval'}
              className="px-3 py-1 text-xs border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 transition-colors font-semibold disabled:opacity-50"
            >
              {sendingSapApproval ? 'Sending…' : sapQueuedOrSent ? 'Sent to SAP' : 'Send to SAP Approval'}
            </button>
          )}
          {!isNew && (
            <button
              type="button"
              onClick={handleSendApprovalEmail}
              disabled={sendingEmailNotification}
              title="Send PO approval email notification to Approver and Sender"
              className="px-3 py-1 text-xs border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors font-semibold disabled:opacity-50 rounded-lg flex items-center gap-1"
            >
              <span>✉</span> {sendingEmailNotification ? 'Sending Email…' : 'Send Approval Email'}
            </button>
          )}
          {!isNew && form.status === 'Draft' && (
            <button onClick={() => handleSave('Open')} disabled={saving}
              className="btn-primary text-xs py-1.5 px-3 disabled:opacity-60">
              Confirm Order
            </button>
          )}
          <button onClick={() => handleSave()} disabled={saving}
            className="btn-primary text-xs py-1.5 px-3 disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
          {!isNew && (
            <button onClick={handleDelete} disabled={saving}
              className="px-3 py-1 text-xs border border-red-300 text-red-500 rounded-lg hover:bg-red-50 transition-colors font-semibold disabled:opacity-50">
              Delete
            </button>
          )}
          <button onClick={() => navigate('/purchasing/orders')}
            className="px-3 py-1 text-xs border border-sap-border text-sap-textLight rounded-lg hover:bg-gray-50 transition-colors">
            Back
          </button>
        </div>
      </div>

      {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">{error}</div>}
      {successMsg && <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">{successMsg}</div>}
      {!isNew && (sapSyncMeta.status || sapSyncMeta.docEntry || sapSyncMeta.draftEntry || sapSyncMeta.error) && (
        <div className={`px-3 py-2 rounded-lg text-xs border ${sapSyncMeta.error ? 'bg-red-50 border-red-200 text-red-700' : sapQueuedOrSent ? 'bg-indigo-50 border-indigo-200 text-indigo-800' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
          <div className="font-semibold">SAP Approval Sync</div>
          {sapStandingText && <div className="mt-0.5">{sapStandingText}</div>}
          <div className="mt-0.5">
            Status: {sapSyncMeta.status || '—'}
            {sapSyncMeta.docNum != null ? ` · DocNum ${sapSyncMeta.docNum}` : ''}
            {sapSyncMeta.docEntry != null ? ` · DocEntry ${sapSyncMeta.docEntry}` : ''}
            {sapSyncMeta.docEntry == null && sapSyncMeta.draftEntry != null
              ? ` · Draft ${sapSyncMeta.draftEntry}` : ''}
          </div>
          {sapSyncMeta.error && <div className="mt-0.5">Error: {sapSyncMeta.error}</div>}
        </div>
      )}

      {/* ════════════ PERSISTENT HEADER ════════════ */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {/* Left column */}
          <div className="space-y-2">
            <F label="Vendor">
              <div className="relative" ref={vendorWrapRef}>
                {form.vendor_code ? (
                  <div className={`${inp} flex items-center gap-2`}>
                    <span className="font-medium text-sap-blue">{form.vendor_code}</span>
                    <span className="text-sap-textLight truncate flex-1">{form.vendor_name}</span>
                    <button onClick={() => { setField('vendor_id', ''); setField('vendor_code', ''); setField('vendor_name', ''); }}
                      className="text-red-400 hover:text-red-600 text-xs">✕</button>
                  </div>
                ) : (
                  <div className="flex items-stretch w-full">
                    <input
                      value={vendorSearch}
                      placeholder="Vendor code or name…"
                      onChange={e => { setVendorSearch(e.target.value); setShowVendorDD(true); }}
                      onFocus={() => setShowVendorDD(true)}
                      onBlur={vendorFieldBlurLater}
                      className="flex-1 min-w-0 h-[28px] px-2 text-xs border border-sap-border rounded-l bg-white border-r-0
                                 focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue focus:z-10"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      title="Show vendors"
                      aria-label="Open vendor list"
                      onMouseDown={e => {
                        e.preventDefault();
                        setShowVendorDD(true);
                        setTimeout(() => vendorDdSearchRef.current?.focus({ preventScroll: true }), 0);
                      }}
                      className="h-[28px] w-8 shrink-0 flex items-center justify-center border border-sap-border rounded-r
                                 bg-gray-50 text-sap-textLight hover:bg-sap-blue/10 hover:text-sap-blue hover:border-sap-blue/40 transition-colors">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>
                )}
                {showVendorDD && !form.vendor_code && (
                  <div className="absolute z-50 left-0 top-full mt-0.5 w-full flex flex-col bg-white border border-sap-border rounded-lg shadow-xl overflow-hidden max-h-64">
                    <div className="shrink-0 p-2 border-b border-sap-border bg-slate-50/95">
                      <div className="relative">
                        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-sap-textLight pointer-events-none" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                          <circle cx="11" cy="11" r="8" />
                          <path d="M21 21l-4.3-4.3" />
                        </svg>
                        <input
                          ref={vendorDdSearchRef}
                          type="search"
                          value={vendorSearch}
                          onChange={e => { setVendorSearch(e.target.value); setShowVendorDD(true); }}
                          onFocus={() => setShowVendorDD(true)}
                          onBlur={vendorFieldBlurLater}
                          placeholder="Search vendor code or name…"
                          autoComplete="off"
                          className="w-full h-8 pl-8 pr-2 text-xs border border-sap-border rounded-md bg-white
                                     focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue"
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto min-h-[2.5rem] max-h-[11.5rem]">
                      {vendorListLoading && (
                        <div className="px-3 py-3 text-xs text-sap-textLight text-center">Loading…</div>
                      )}
                      {!vendorListLoading && vendorResults.length === 0 && (
                        <div className="px-3 py-2 text-xs text-sap-textLight">No vendors found</div>
                      )}
                      {!vendorListLoading && vendorResults.map(v => (
                        <button key={v.vendor_id} type="button" onMouseDown={() => pickVendor(v)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-sap-blue/10 flex items-center gap-2 border-t border-sap-border/30 first:border-t-0">
                          <span className="font-semibold text-sap-blue">{v.vendor_code}</span>
                          <span className="text-sap-textLight truncate">{v.vendor_name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </F>
            <F label="Name">
              <input value={form.vendor_name} readOnly className={`${inp} bg-gray-50 text-sap-textLight`} />
            </F>
            <F label="Contact Person">
              <input value={form.contact_person} onChange={e => setField('contact_person', e.target.value)}
                className={inp} />
            </F>
            <F label="Vendor Ref. No.">
              <input value={form.vendor_ref_no} onChange={e => setField('vendor_ref_no', e.target.value)}
                className={inp} />
            </F>
            <F label="Local Currency">
              <select value={form.currency} onChange={e => setField('currency', e.target.value)} className={sel}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </F>
            <F label="Document Series">
              <select value={form.department || ''} onChange={(e) => setField('department', e.target.value)} className={sel}>
                <option value="">— Select —</option>
                {poDepartmentOpts.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </F>
          </div>

          {/* Right column */}
          <div className="space-y-2">
            <F label="No.">
              <div className="flex gap-1">
                {isNew ? (
                  <div className="relative flex-shrink-0 flex-grow-0">
                    <select
                      value={selectedSeriesId || ''}
                      onChange={e => handleSeriesChange(e.target.value)}
                      className="w-[110px] h-[28px] pl-2 pr-6 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue appearance-none">
                      {poSeriesList
                        .filter(s => {
                          const maxYear = poSeriesList.reduce((m, x) => x.year_code > m ? x.year_code : m, '');
                          return s.year_code === maxYear;
                        })
                        .map(s => (
                          <option key={s.series_id} value={s.series_id}>{s.series_name}</option>
                        ))}
                    </select>
                    <svg className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sap-textLight" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                ) : (
                  <input value={form.series} readOnly
                    className="w-[110px] h-[28px] px-2 text-xs border border-sap-border rounded bg-gray-50 text-sap-textLight flex-shrink-0 flex-grow-0" />
                )}
                <input value={form.po_number} readOnly
                  className={`${inp} flex-1 bg-gray-50 text-sap-textLight`} />
              </div>
            </F>
            <F label="Status">
              {/* The status is the document's own: Draft until SAP adds it, Open once
                  the approval sync sees it there. Typing over it would only make the
                  screen disagree with SAP, so it is shown rather than offered. */}
              <input
                value={form.status || 'Draft'}
                readOnly
                title="Set by the document: Draft while it waits in SAP, Open once SAP has added it"
                className={inp + ' bg-sap-bg/60 cursor-default'}
              />
              <select value={form.status} onChange={e => setField('status', e.target.value)} className={sel} hidden>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </F>
            <F label="Posting Date">
              <input type="date" {...DATE_BOUNDS} value={form.posting_date} onChange={e => setField('posting_date', e.target.value)}
                className={inp} />
            </F>
            <F label="Delivery Date">
              <input type="date" {...DATE_BOUNDS} value={form.delivery_date} onChange={e => setField('delivery_date', e.target.value)}
                className={inp} />
            </F>
            <F label="Document Date">
              <input type="date" {...DATE_BOUNDS} value={form.document_date} onChange={e => setField('document_date', e.target.value)}
                className={inp} />
            </F>
            <F label="Approver Name">
              <select
                value={form.approver_name || ''}
                onChange={(e) => {
                  if (e.target.value === '__ADD_NEW__') {
                    if (isSuperAdmin) {
                      openAddApproverModal();
                    } else {
                      alert('Only Super Admin users can add new approvers.');
                    }
                  } else {
                    const chosen = e.target.value;
                    const expected = approverRule.approvers.length === 1 ? approverRule.approvers[0] : null;
                    if (expected && chosen && chosen !== expected) {
                      const ok = window.confirm(
                        `The items on this PO (${approverRule.groups.join(', ')}) are approved by ${expected}.\n\n`
                        + `The approval email will go to ${chosen} instead. Continue?`
                      );
                      if (!ok) return;
                      approverOverridden.current = true;
                    } else {
                      approverOverridden.current = false;
                    }
                    setField('approver_name', chosen);
                  }
                }}
                className={sel}
              >
                <option value="">— Select —</option>
                {approverOptions.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
                {form.approver_name && !approverOptions.includes(form.approver_name) && (
                  <option value={form.approver_name}>{form.approver_name}</option>
                )}
                {isSuperAdmin && (
                  <option value="__ADD_NEW__">+ Add New Approver...</option>
                )}
              </select>
              {approverRule.mixed ? (
                <p className="mt-1 text-[11px] text-amber-700">
                  ⚠ This PO mixes items for {approverRule.approvers.join(' and ')}. Please split it into separate POs.
                </p>
              ) : approverRule.approvers.length === 1 && form.approver_name === approverRule.approvers[0] ? (
                <p className="mt-1 text-[11px] text-sap-textLight">
                  {/* Say which rule decided it. "Set from item group" on an order routed
                      by its distribution rule would name the wrong reason, and the item
                      group had no say at all. */}
                  {approverRule.decided_by === 'distribution_rule'
                    ? `Set from distribution rule: ${approverRule.distribution_rule}`
                    : `Set from item group: ${approverRule.groups.join(', ')}`}
                </p>
              ) : approverRule.approvers.length === 1 && form.approver_name ? (
                <p className="mt-1 text-[11px] text-amber-700">
                  ⚠ Items on this PO ({approverRule.groups.join(', ')}) are approved by {approverRule.approvers[0]}.
                  The approval email will go to {form.approver_name}.
                </p>
              ) : null}
            </F>
          </div>
        </div>
      </div>

      {/* ════════════ TABS ════════════ */}
      <div className="flex gap-0 border-b border-sap-border/60">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-xs font-medium transition-colors border-b-2 -mb-px
              ${activeTab === i ? 'border-sap-blue text-sap-blue' : 'border-transparent text-sap-textLight hover:text-sap-text'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ─── Tab 0 : Contents ─── */}
      {activeTab === 0 && (
        <div className="space-y-2">
          <div className="bg-white rounded-2xl shadow-sm p-3 flex justify-between items-end gap-4 flex-wrap">
            <F label="Item/Service Type">
              <select value={form.item_service_type} onChange={e => setField('item_service_type', e.target.value)} className={`${sel} w-32`}>
                {ITEM_SERVICE_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </F>
            <div className="flex items-end gap-3 flex-wrap">
              <F label="Summary Type">
                <select value={form.summary_type} onChange={e => setField('summary_type', e.target.value)} className={`${sel} w-40`}>
                  {SUMMARY_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </F>
              {!poContentsLocked && (
                <button
                  type="button"
                  onClick={() => setShowMrpImport(true)}
                  className="h-[28px] px-3 text-xs font-semibold border border-sap-blue text-sap-blue rounded bg-white hover:bg-sap-blue/5 whitespace-nowrap shrink-0">
                  Import from order recommendation
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
            <table className="w-full text-xs min-w-[2100px]">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Type</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Item No.</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">BP Catalog No.</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Item Description</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Quantity</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">UoM Name</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Unit Price</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Discount %</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">Price after Discount</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Tax Code</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Total (LC)</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Project</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">Distribution Rule</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-32">Warehouse</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-36">PO Delivery Date</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">HSN</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Open Qty</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">In Stock</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-24">Committed</th>
                  <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-20">Ordered</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-36">Country/Region of Origin</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-32">Blanket Agreement No.</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-40">Standard Item Identification</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-36">Commodity Classification</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const lineTotal = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0) * (1 - (parseFloat(l.discount_pct) || 0) / 100);
                  return (
                    <tr key={l._key} className="border-t border-sap-border/30 hover:bg-sap-blueLight/20">
                      <td className="px-3 py-1 text-sap-textLight">{idx + 1}</td>
                      <td className="px-1 py-1">
                        <select value={l.line_type} onChange={e => updateLine(idx, 'line_type', e.target.value)}
                          className="w-20 border border-sap-border rounded px-1 py-1 text-xs bg-white focus:outline-none focus:border-sap-blue">
                          {LINE_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <div className="flex items-center gap-1">
                          {l.item_no && (
                            <SapYellowArrow
                              title="Link to Item Master"
                              onClick={() => navigate(`/item-master/${l.item_no}`)}
                            />
                          )}
                          <input value={itemSearchIdx === idx ? itemSearch : l.item_no}
                            ref={(el) => {
                              if (itemSearchIdx === idx) activeItemInputRef.current = el;
                            }}
                            onChange={e => {
                              activeItemInputRef.current = e.currentTarget;
                              setItemSearch(e.target.value);
                              setItemSearchIdx(idx);
                              setShowItemDD(true);
                            }}
                            onFocus={(e) => {
                              if (itemBlurTimerRef.current) {
                                clearTimeout(itemBlurTimerRef.current);
                                itemBlurTimerRef.current = null;
                              }
                              activeItemInputRef.current = e.currentTarget;
                              setItemSearchIdx(idx);
                              setItemSearch(l.item_no || '');
                              setShowItemDD(true);
                            }}
                            onBlur={() => {
                              itemBlurTimerRef.current = setTimeout(() => {
                                setShowItemDD(false);
                                setItemSearchIdx(null);
                                setItemSearch('');
                                itemBlurTimerRef.current = null;
                              }, 200);
                            }}
                            placeholder="Search…"
                            autoComplete="off"
                            className="w-28 min-w-[7rem] border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                        </div>
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.bp_catalog_no} onChange={e => updateLine(idx, 'bp_catalog_no', e.target.value)}
                          className="w-28 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.item_desc} readOnly
                          className="w-full border border-sap-border rounded px-2 py-1 text-xs bg-gray-50 text-sap-textLight min-w-[140px]" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)}
                          className="w-20 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <select value={l.uom} onChange={e => updateLine(idx, 'uom', e.target.value)}
                          className="w-24 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue bg-white">
                          {UOM_OPTIONS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                          className="w-24 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.discount_pct} onChange={e => updateLine(idx, 'discount_pct', e.target.value)}
                          className="w-16 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-3 py-1 text-right font-semibold">{fmtCurrency(lineTotal)}</td>
                      <SapReferenceCell
                        value={l.tax_code}
                        onChange={(v) => updateLine(idx, 'tax_code', v)}
                        onOpen={() => setRefPicker({ type: 'tax', field: 'tax_code', idx })}
                        title="Tax Code"
                        width="w-20"
                      />
                      <td className="px-3 py-1 text-right font-semibold">{fmtCurrency(lineTotal)}</td>
                      <ProjectCell
                        value={l.project}
                        onChange={(v) => updateLine(idx, 'project', v)}
                      />
                      <SapReferenceCell
                        value={l.distr_rule}
                        onChange={(v) => updateLine(idx, 'distr_rule', v)}
                        onOpen={() => setRefPicker({ type: 'distribution', field: 'distr_rule', idx })}
                        title="Distribution Rule"
                        width="w-28"
                      />
                      <td className="px-1 py-1">
                        <select
                          value={l.warehouse_id}
                          onChange={e => {
                            const wh = warehouses.find(w => String(w.warehouse_id) === e.target.value);
                            updateLine(idx, 'warehouse_id', e.target.value);
                            updateLine(idx, 'wh_code', wh?.wh_code || '');
                          }}
                          className="w-32 border border-sap-border rounded px-1 py-1 text-xs bg-white focus:outline-none focus:border-sap-blue"
                        >
                          <option value="">— None —</option>
                          {warehouses.map(w => (
                            <option key={w.warehouse_id} value={String(w.warehouse_id)}>
                              {w.wh_code}{w.wh_name ? ` — ${w.wh_name}` : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-1">
                        {/* Native date input: shows the calendar icon and picker. Sent to SAP as the line's Del. Date. */}
                        <input type="date" {...DATE_BOUNDS} value={l.delivery_date || ''} onChange={e => updateLine(idx, 'delivery_date', e.target.value)}
                          className="w-36 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <div className="relative w-28">
                          <input value={l.hsn_code || ''} maxLength={20} placeholder="HSN"
                            onChange={e => updateLine(idx, 'hsn_code', e.target.value)}
                            className="w-full border border-sap-border rounded pl-2 pr-6 py-1 text-xs
                              focus:outline-none focus:border-sap-blue" />
                          <HsnListButton
                            onClick={() => setHsnPickerLine(idx)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 rounded
                              hover:bg-sap-bg"
                          />
                        </div>
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.open_qty} onChange={e => updateLine(idx, 'open_qty', e.target.value)}
                          className="w-20 border border-sap-border rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.in_stock} readOnly
                          className="w-20 border border-sap-border rounded px-2 py-1 text-xs text-right bg-gray-50 text-sap-textLight" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.committed} readOnly
                          className="w-20 border border-sap-border rounded px-2 py-1 text-xs text-right bg-gray-50 text-sap-textLight" />
                      </td>
                      <td className="px-1 py-1">
                        <input type="number" value={l.ordered} readOnly
                          className="w-20 border border-sap-border rounded px-2 py-1 text-xs text-right bg-gray-50 text-sap-textLight" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.country_of_origin} onChange={e => updateLine(idx, 'country_of_origin', e.target.value)}
                          className="w-36 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.blanket_agreement_no} onChange={e => updateLine(idx, 'blanket_agreement_no', e.target.value)}
                          className="w-32 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.standard_item_id} onChange={e => updateLine(idx, 'standard_item_id', e.target.value)}
                          className="w-40 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={l.commodity_classification} onChange={e => updateLine(idx, 'commodity_classification', e.target.value)}
                          className="w-36 border border-sap-border rounded px-2 py-1 text-xs focus:outline-none focus:border-sap-blue" />
                      </td>
                      <td className="px-2 py-1">
                        <button onClick={() => removeLine(idx)} disabled={lines.length === 1}
                          className="text-red-400 hover:text-red-600 transition-colors disabled:opacity-30" title="Remove">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button onClick={addLine}
            className="w-full py-1.5 border border-dashed border-sap-blue/40 rounded-xl text-xs text-sap-blue hover:bg-sap-blue/5 transition-colors font-medium">
            + Add Line
          </button>
        </div>
      )}

      {/* ─── Tab 1 : Logistics ─── */}
      {activeTab === 1 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <div className="space-y-3">
              <F label="Ship To">
                <textarea value={form.ship_to_address} onChange={e => setField('ship_to_address', e.target.value)}
                  rows={3} className={`${inp} h-auto py-1.5 resize-none`} placeholder="Shipping address" />
              </F>
              <F label="Pay To">
                <textarea value={form.bill_to_address} onChange={e => setField('bill_to_address', e.target.value)}
                  rows={3} className={`${inp} h-auto py-1.5 resize-none`} placeholder="Billing address" />
              </F>
              <F label="Shipping Type">
                <select value={form.shipping_type} onChange={e => setField('shipping_type', e.target.value)} className={sel}>
                  {SHIPPING_TYPES.map(s => <option key={s} value={s}>{s || '—'}</option>)}
                </select>
              </F>
            </div>
            <div className="space-y-3">
              <F label="Language">
                <select value={form.language} onChange={e => setField('language', e.target.value)} className={sel}>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l || '—'}</option>)}
                </select>
              </F>
              <div className="space-y-2 pt-1">
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.split_purchase_order} onChange={e => setField('split_purchase_order', e.target.checked)} className={chk} />
                  Split Purchase Order
                </label>
                <label className="flex items-center gap-2 text-xs text-sap-text">
                  <input type="checkbox" checked={form.approved} onChange={e => setField('approved', e.target.checked)} className={chk} />
                  Approved
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tab 2 : Accounting ─── */}
      {activeTab === 2 && (
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <div className="space-y-3">
              <F label="Journal Remark">
                <input value={form.journal_remark} onChange={e => setField('journal_remark', e.target.value)} className={inp} />
              </F>
              <F label="Payment Terms">
                <select value={form.payment_terms} onChange={e => setField('payment_terms', e.target.value)} className={sel}>
                  {PAY_TERMS.map(t => <option key={t} value={t}>{t || '—'}</option>)}
                </select>
              </F>
              <F label="Payment Method">
                <select value={form.payment_method} onChange={e => setField('payment_method', e.target.value)} className={sel}>
                  {PAY_METHODS.map(m => <option key={m} value={m}>{m || '—'}</option>)}
                </select>
              </F>
              <F label="Central Bank Ind.">
                <input value={form.central_bank_ind} onChange={e => setField('central_bank_ind', e.target.value)} className={inp} />
              </F>
              <div>
                <label className={lbl}>Manually Recalculate Due Date</label>
                <div className="flex gap-2">
                  <input value={form.due_date_type} onChange={e => setField('due_date_type', e.target.value)}
                    placeholder="Type" className={`${inp} w-28`} />
                  <div className="flex items-center gap-1">
                    <span className="text-2xs text-sap-textLight">Months</span>
                    <input type="number" value={form.due_date_months} onChange={e => setField('due_date_months', e.target.value)}
                      className={`${inp} w-14 text-center`} />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-2xs text-sap-textLight">Days</span>
                    <input type="number" value={form.due_date_days} onChange={e => setField('due_date_days', e.target.value)}
                      className={`${inp} w-14 text-center`} />
                  </div>
                </div>
              </div>
              <F label="Cash Discount Date Offset">
                <input type="number" value={form.cash_discount_offset} onChange={e => setField('cash_discount_offset', e.target.value)}
                  className={inp} />
              </F>
            </div>
            <div className="space-y-3">
              <F label="Business Partner Project">
                <BusinessPartnerProjectField value={form.bp_project} onChange={v => setField('bp_project', v)} />
              </F>
              <F label="Create QR Code From">
                <input value={form.create_qr_code_from} onChange={e => setField('create_qr_code_from', e.target.value)} className={inp} />
              </F>
              <F label="Cancellation Date">
                <input type="date" {...DATE_BOUNDS} value={form.cancellation_date} onChange={e => setField('cancellation_date', e.target.value)} className={inp} />
              </F>
              <F label="Required Date">
                <input type="date" {...DATE_BOUNDS} value={form.required_date} onChange={e => setField('required_date', e.target.value)} className={inp} />
              </F>
              <F label="Indicator">
                <input value={form.indicator} onChange={e => setField('indicator', e.target.value)} className={inp} />
              </F>
              <F label="Federal Tax ID">
                <input value={form.federal_tax_id} onChange={e => setField('federal_tax_id', e.target.value)} className={inp} />
              </F>
              <F label="Order Number">
                <input value={form.order_number} onChange={e => setField('order_number', e.target.value)} className={inp} />
              </F>
              <F label="Referenced Document">
                <input value={form.referenced_document} onChange={e => setField('referenced_document', e.target.value)} className={inp} />
              </F>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tab 3 : Attachments ─── */}
      {activeTab === 3 && (
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[600px]">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Target Path</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">File Name</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-28">Attachment Date</th>
                  <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Free Text</th>
                  <th className="text-center px-3 py-2 font-semibold text-2xs uppercase tracking-wider w-32">Copy to Target Doc.</th>
                </tr>
              </thead>
              <tbody>
                {attachments.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-sap-textLight">No attachments</td></tr>
                ) : attachments.map((a, i) => (
                  <tr key={a.attachment_id} className="border-t border-sap-border/30">
                    <td className="px-3 py-1.5">{i + 1}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{a.target_path || '—'}</td>
                    <td className="px-3 py-1.5 font-medium">{a.file_name}</td>
                    <td className="px-3 py-1.5">{a.attachment_date?.slice(0, 10) || a.uploaded_at?.slice(0, 10) || ''}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{a.free_text || ''}</td>
                    <td className="px-3 py-1.5 text-center">{a.copy_to_target ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-1.5 text-xs border border-sap-border rounded-lg hover:bg-gray-50 transition-colors text-sap-text">
              Browse
            </button>
            <button className="px-3 py-1.5 text-xs border border-sap-border rounded-lg hover:bg-gray-50 transition-colors text-sap-text">
              Display
            </button>
            <button className="px-3 py-1.5 text-xs border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition-colors">
              Delete
            </button>
          </div>
        </div>
      )}

      {/* ════════════ PERSISTENT FOOTER ════════════ */}
      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <F label="Buyer">
                <SapEmployeeSelectWithArrow
                  value={form.buyer}
                  onChange={(v) => setField('buyer', v)}
                  selectClassName={`flex-1 min-w-0 ${sel}`}
                />
              </F>
              <F label="Owner">
                {/* The people who use this system and SAP's own employees. An owner
                    matched to a SAP employee reaches SAP as the document's owner. */}
                <ActiveUserSelectWithArrow
                  value={form.owner}
                  onChange={(v) => setField('owner', v)}
                  usersUrl="/purchase-orders/owners"
                  selectClassName={`flex-1 min-w-0 ${sel}`}
                />
              </F>
            </div>
            <F label="Remarks">
              <textarea value={form.remarks} onChange={e => setField('remarks', e.target.value)} rows={2}
                className={`${inp} h-auto py-1.5 resize-none`} />
            </F>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between items-center text-sap-textLight">
              <span>Total Before Discount</span>
              <span className="font-medium">{fmtCurrency(totalBeforeDiscount)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight gap-2">
              <span className="flex items-center gap-1">Discount %
                <input type="number" value={form.discount_pct} onChange={e => setField('discount_pct', e.target.value)}
                  className="w-14 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
              </span>
              <span>-{fmtCurrency(discountAmt)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight">
              <span className="flex items-center gap-1">Freight
                <input type="number" value={form.freight_charges} onChange={e => setField('freight_charges', e.target.value)}
                  className="w-20 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
              </span>
              <span>{fmtCurrency(freight)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight gap-2">
              <span className="flex items-center gap-1">
                <input type="checkbox" checked={form.rounding} onChange={e => setField('rounding', e.target.checked)} className={chk} />
                Rounding
                {form.rounding && (
                  <input type="number" value={form.rounding_amount} onChange={e => setField('rounding_amount', e.target.value)}
                    className="w-16 border border-sap-border rounded px-1 py-0.5 text-xs text-right focus:outline-none focus:border-sap-blue" />
                )}
              </span>
              <span>{fmtCurrency(roundingVal)}</span>
            </div>
            <div className="flex justify-between items-center text-sap-textLight">
              <span>Tax</span>
              <span>{fmtCurrency(taxAmt)}</span>
            </div>
            <div className="flex justify-between font-bold text-sap-text border-t border-sap-border/60 pt-1 text-sm">
              <span>Total Payment Due ({form.currency})</span>
              <span>{fmtCurrency(grandTotal)}</span>
            </div>
          </div>
        </div>
      </div>

      {showItemDD && itemSearchIdx !== null && itemDDRect != null &&
        createPortal(
          <div
            role="listbox"
            className="pointer-events-auto bg-white border border-sap-border rounded-lg shadow-[0_12px_40px_rgba(0,0,0,0.18)] overflow-y-auto"
            style={{
              position: 'fixed',
              top: itemDDRect.top,
              left: itemDDRect.left,
              width: itemDDRect.width,
              maxHeight: itemDDRect.maxHeight,
              zIndex: 100050,
            }}
          >
            {itemSearchLoading ? (
              <div className="px-3 py-2 text-2xs text-sap-textLight">Loading items…</div>
            ) : itemResults.length === 0 ? (
              <div className="px-3 py-2 text-2xs text-sap-textLight">
                {itemSearch.trim()
                  ? 'No matching active items. Try item number, description, or catalog code.'
                  : 'No active items found for PO lines. Use Item Master — ensure status Active (and not Purchase Item = No).'}
              </div>
            ) : (
              itemResults.map((it) => (
                <button
                  key={it.item_id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pickItem(it); }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-sap-blue/10 flex flex-col gap-0.5 border-b border-sap-border/30 last:border-0">
                  <span>
                    <span className="font-semibold text-sap-blue">{it.item_no}</span>
                    {it.bp_catalog_hint ? (
                      <span className="text-sap-textLight ml-2 text-2xs">Cat: {it.bp_catalog_hint}</span>
                    ) : null}
                  </span>
                  <span className="text-sap-textLight truncate">{it.description}</span>
                </button>
              ))
            )}
          </div>,
          document.body
        )}

      {showMrpImport && (
        <MrpRecommendationImportDialog
          open={showMrpImport}
          onClose={() => setShowMrpImport(false)}
          mode="purchase"
          onApply={applyMrpImportLines}
        />
      )}

      {/* Modal: Add New Approver */}
      {showAddApproverModal && (
        <div className="fixed inset-0 z-[100100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-5 py-3.5 bg-sap-blue text-white flex items-center justify-between">
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <span>+ Add New Approver</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowAddApproverModal(false)}
                className="text-white/80 hover:text-white text-lg font-bold leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-semibold text-sap-text mb-1">
                  Approver Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newApproverName}
                  onChange={(e) => setNewApproverName(e.target.value)}
                  placeholder="Type or select an employee name below..."
                  className="w-full h-8 px-3 text-xs border border-sap-border rounded focus:outline-none focus:ring-1 focus:ring-sap-blue"
                />
              </div>

              {/* Admin Panel Employees Picker */}
              <div className="border border-sap-border/70 rounded-lg p-3 bg-gray-50/50 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-sap-text">
                    Select Employee from Admin Panel
                  </span>
                  <span className="text-2xs text-sap-textLight font-medium">
                    {adminEmployees.length} employee(s) available
                  </span>
                </div>

                <input
                  type="text"
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                  placeholder="Search employee name, email or role..."
                  className="w-full h-7 px-2.5 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40"
                />

                <div className="max-h-48 overflow-y-auto border border-sap-border rounded bg-white divide-y divide-gray-100 text-xs">
                  {loadingAdminEmployees ? (
                    <div className="p-4 text-center text-sap-textLight text-xs">Loading employees from Admin Panel...</div>
                  ) : adminEmployees.filter(e =>
                      !empSearch.trim() ||
                      (e.name && e.name.toLowerCase().includes(empSearch.toLowerCase())) ||
                      (e.email && e.email.toLowerCase().includes(empSearch.toLowerCase())) ||
                      (e.role_name && e.role_name.toLowerCase().includes(empSearch.toLowerCase()))
                    ).length === 0 ? (
                    <div className="p-4 text-center text-sap-textLight text-xs">No matching employees found</div>
                  ) : (
                    adminEmployees
                      .filter(e =>
                        !empSearch.trim() ||
                        (e.name && e.name.toLowerCase().includes(empSearch.toLowerCase())) ||
                        (e.email && e.email.toLowerCase().includes(empSearch.toLowerCase())) ||
                        (e.role_name && e.role_name.toLowerCase().includes(empSearch.toLowerCase()))
                      )
                      .map((emp) => {
                        const isSelected = newApproverName === emp.name;
                        return (
                          <button
                            key={emp.user_id}
                            type="button"
                            onClick={() => setNewApproverName(emp.name)}
                            className={`w-full text-left px-3 py-2 flex items-center justify-between hover:bg-sap-blue/5 transition-colors ${
                              isSelected ? 'bg-sap-blue/10 text-sap-blue font-semibold' : 'text-sap-text'
                            }`}
                          >
                            <div>
                              <div className="font-medium text-xs">{emp.name}</div>
                              {emp.email && <div className="text-[10px] text-sap-textLight">{emp.email}</div>}
                            </div>
                            {emp.role_name && (
                              <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 text-sap-textLight font-normal">
                                {emp.role_name}
                              </span>
                            )}
                          </button>
                        );
                      })
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-3 bg-gray-50 border-t border-sap-border flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddApproverModal(false)}
                className="px-3 py-1.5 text-xs text-sap-text font-medium hover:bg-gray-200 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveNewApprover}
                disabled={addingApprover || !newApproverName.trim()}
                className="px-4 py-1.5 text-xs bg-sap-blue text-white font-semibold rounded hover:bg-sap-blue/90 disabled:opacity-50 transition-colors shadow-sm"
              >
                {addingApprover ? 'Adding...' : '+ Add Approver'}
              </button>
            </div>
          </div>
        </div>
      )}

      {refPicker && (
        <SapReferencePickerDialog
          type={refPicker.type}
          current={lines[refPicker.idx]?.[refPicker.field] || ''}
          onSelect={(row) => {
            const code = refPicker.type === 'tax' ? row.tax_code : row.rule_code;
            updateLine(refPicker.idx, refPicker.field, code || '');
            setRefPicker(null);
          }}
          onClose={() => setRefPicker(null)}
        />
      )}
      {/* SAP's HSN list, opened from a line's HSN field */}
      {hsnPickerLine !== null && (
        <HsnChooser
          codes={hsnCodes}
          current={lines[hsnPickerLine]?.hsn_code || ''}
          onPick={(code) => { updateLine(hsnPickerLine, 'hsn_code', code); setHsnPickerLine(null); }}
          onClose={() => setHsnPickerLine(null)}
        />
      )}
    </div>
  );
}

function F({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className={lbl}>{label}</label>
      {children}
    </div>
  );
}
